import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Menu,
  Select,
  Space,
  Stack,
  TagsInput,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {useForm} from "@mantine/form";
import {
  IconChevronDown,
  IconChevronUp,
  IconLanguage,
  IconSearch,
  IconTrash,
  IconWand,
} from "@tabler/icons-react";
import {useCallback, useState} from "react";
import {
  isHiragana,
  isKanji,
  isKatakana,
  isRomaji,
  toHiragana,
  toKatakana,
  toRomaji,
} from "wanakana";
import type {LibraryEntry, Reading, SRSState} from "@/shared/types";
import {getKanjiSuggestions} from "../lib/kanjiApi";

const DEFAULT_SRS_STATE: SRSState = {
  ease: 250,
  intervalDays: 0,
  dueAt: new Date().toISOString(),
  repetition: 0,
};

interface FormValues {
  type: "vocab" | "character";
  languageCode: string;
  expression: string;
  meaning: string;
  readings: Reading[];
  grouping: string;
  tags: string[];
  auxiliary: string;
}

export function LibraryEntryForm(props: {
  initialValues?: LibraryEntry;
  onSubmit: (entry: Omit<LibraryEntry, "createdAt" | "updatedAt">) => void;
  loading?: boolean;
}) {
  const {initialValues, onSubmit, loading} = props;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      type: initialValues?.type || "vocab",
      languageCode: initialValues?.languageCode || "ja",
      expression: initialValues?.content.expression || "",
      meaning: initialValues?.content.meaning || "",
      readings: initialValues?.content.readings || [],
      grouping: initialValues?.grouping || "",
      tags: initialValues?.tags || [],
      auxiliary: initialValues?.content.auxiliary || "",
    },
    validate: {
      expression: value => (value.trim().length > 0 ? null : "Expression is required"),
      meaning: value => (value.trim().length > 0 ? null : "Meaning is required"),
      languageCode: value =>
        value.trim().length > 0 ? null : "Language code is required",
    },
  });

  const handleSubmit = (values: FormValues) => {
    onSubmit({
      type: values.type,
      languageCode: values.languageCode,
      grouping: values.grouping || undefined,
      tags: values.tags.length > 0 ? values.tags : undefined,
      content: {
        expression: values.expression,
        meaning: values.meaning,
        readings: values.readings.length > 0 ? values.readings : undefined,
        auxiliary: values.auxiliary || undefined,
      },
      srsState: initialValues?.srsState || DEFAULT_SRS_STATE,
    });
  };

  const handleSuggestKanji = async () => {
    // Find the first kana reading to use for suggestions
    const reading = form.values.readings.find(
      r => r.type === "kana" || r.type === "on" || r.type === "kun",
    );
    const textToSearch = reading?.value || form.values.expression;

    if (!textToSearch) return;

    setIsSearching(true);
    // Convert to hiragana first to ensure API works best
    const hira = toHiragana(textToSearch);
    const results = await getKanjiSuggestions(hira);
    setSuggestions(results);
    setIsSearching(false);
  };

  const handleTransliterate = (index: number) => {
    const reading = form.values.readings[index];
    if (!reading?.value) return;

    let newValue = reading.value;
    if (reading.type === "on") {
      newValue = toKatakana(reading.value);
    } else if (reading.type === "kun" || reading.type === "kana") {
      newValue = toHiragana(reading.value);
    } else if (reading.type === "romaji") {
      newValue = toRomaji(reading.value);
    }

    form.setFieldValue(`readings.${index}.value`, newValue);
  };

  const moveReading = (index: number, direction: "up" | "down") => {
    const readings = [...form.values.readings];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= readings.length) return;

    [readings[index], readings[newIndex]] = [readings[newIndex], readings[index]];
    form.setFieldValue("readings", readings);
  };

  const deriveReadings = useCallback(() => {
    const expr = form.values.expression.trim();
    if (!expr || form.values.languageCode !== "ja") return;

    const newReadings: Reading[] = [];

    if (isKanji(expr)) {
      const hiraganaReadings: string[] = [];
      const katakanaReadings: string[] = [];

      for (const char of expr) {
        if (isHiragana(char)) hiraganaReadings.push(char);
        if (isKatakana(char)) katakanaReadings.push(char);
      }

      if (hiraganaReadings.length > 0) {
        newReadings.push({type: "kana", value: hiraganaReadings.join("")});
      }
      if (katakanaReadings.length > 0) {
        newReadings.push({type: "on", value: katakanaReadings.join("")});
      }

      const hiraganaFromKanji = toHiragana(expr);
      if (hiraganaFromKanji !== expr) {
        newReadings.push({type: "kana", value: hiraganaFromKanji});
      }

      const romajiFromExpr = toRomaji(expr);
      if (romajiFromExpr !== expr && isRomaji(romajiFromExpr)) {
        newReadings.push({type: "romaji", value: romajiFromExpr});
      }
    } else if (isHiragana(expr)) {
      newReadings.push({type: "kana", value: expr});
      const romaji = toRomaji(expr);
      if (romaji !== expr) {
        newReadings.push({type: "romaji", value: romaji});
      }
    } else if (isKatakana(expr)) {
      newReadings.push({type: "on", value: expr});
      const hiragana = toHiragana(expr);
      newReadings.push({type: "kana", value: hiragana});
      const romaji = toRomaji(hiragana);
      if (romaji !== expr) {
        newReadings.push({type: "romaji", value: romaji});
      }
    }

    if (newReadings.length > 0) {
      form.setFieldValue("readings", newReadings);
    }
  }, [form.values.expression, form.values.languageCode, form.setFieldValue, form]);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Group grow>
          <Select
            label="Type"
            data={[
              {label: "Vocabulary", value: "vocab"},
              {label: "Character/Kanji", value: "character"},
            ]}
            required
            {...form.getInputProps("type")}
          />
          <Select
            label="Language Code"
            required
            data={[
              {value: "ja", label: "Japanese (ja)"},
              {value: "zh", label: "Chinese (zh)"},
              {value: "en", label: "English (en)"},
            ]}
            allowDeselect={false}
            {...form.getInputProps("languageCode")}
          />
        </Group>

        <Group align="flex-end" gap="xs">
          <TextInput
            label="Expression"
            placeholder="e.g. 猫 or 食べる"
            required
            {...form.getInputProps("expression")}
            style={{flexGrow: 1}}
          />
          {form.values.languageCode === "ja" && (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Tooltip label="Suggest Kanji from Reading">
                  <ActionIcon
                    variant="light"
                    size="lg"
                    loading={isSearching}
                    type="button"
                    onMouseDown={e => {
                      e.preventDefault();
                      handleSuggestKanji();
                    }}
                  >
                    <IconSearch size={20} />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Suggestions</Menu.Label>
                {suggestions.length > 0 ? (
                  suggestions.map(s => (
                    <Menu.Item
                      key={s}
                      onClick={() => form.setFieldValue("expression", s)}
                    >
                      {s}
                    </Menu.Item>
                  ))
                ) : (
                  <Menu.Item disabled>No suggestions</Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
          {form.values.languageCode === "ja" && (
            <Tooltip label="Derive Readings from Expression">
              <ActionIcon
                variant="light"
                size="lg"
                color="grape"
                type="button"
                onClick={deriveReadings}
              >
                <IconWand size={20} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <TextInput
          label="Meaning"
          placeholder="e.g. Cat or To eat"
          required
          {...form.getInputProps("meaning")}
        />

        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <span style={{fontSize: "14px", fontWeight: 500}}>Readings</span>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => form.insertListItem("readings", {type: "kana", value: ""})}
            >
              + Add Reading
            </Button>
          </Group>
          {form.values.readings.map((reading, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: indices are stable enough here for form list management
            <Flex key={index} gap="xs" direction="row" wrap="nowrap" align="center">
              <Select
                data={[
                  {value: "romaji", label: "Romaji"},
                  {value: "kana", label: "Hiragana"},
                  {value: "on", label: "On'yomi"},
                  {value: "kun", label: "Kun'yomi"},
                  {value: "pinyin", label: "Pinyin"},
                  {value: "ipa", label: "IPA"},
                ]}
                required
                {...form.getInputProps(`readings.${index}.type`)}
              />
              <TextInput
                placeholder="Reading value"
                required
                {...form.getInputProps(`readings.${index}.value`)}
                style={{flexGrow: "1"}}
              />
              {["on", "kun", "kana", "romaji"].includes(reading.type) && (
                <Tooltip label="Transliterate">
                  <ActionIcon
                    size="lg"
                    variant="subtle"
                    color="teal"
                    onClick={() => handleTransliterate(index)}
                  >
                    <IconLanguage size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Space w="xs" />
              <Tooltip label="Move Up">
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  onClick={() => moveReading(index, "up")}
                  disabled={index === 0}
                >
                  <IconChevronUp size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Move Down">
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  onClick={() => moveReading(index, "down")}
                  disabled={index === form.values.readings.length - 1}
                >
                  <IconChevronDown size={16} />
                </ActionIcon>
              </Tooltip>
              <Space w="xs" />
              <ActionIcon
                size="lg"
                color="red"
                variant="light"
                onClick={() => form.removeListItem("readings", index)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Flex>
          ))}
        </Stack>

        <TextInput
          label="Grouping (Class/Book)"
          placeholder="e.g. Clase 4, Genki I"
          {...form.getInputProps("grouping")}
        />

        <TagsInput label="Tags" placeholder="Pick tags" {...form.getInputProps("tags")} />

        <Textarea
          label="Notes (Auxiliary)"
          placeholder="Pitch accent, mnemonics, etc."
          {...form.getInputProps("auxiliary")}
        />

        <Button type="submit" loading={loading}>
          {initialValues ? "Update Entry" : "Create Entry"}
        </Button>
      </Stack>
    </form>
  );
}
